/**
 * Authorization Demo Page
 * 
 * Interactive demonstration of the THREE-LAYER authorization system:
 * 
 * 1. CAPABILITY LAYER (Users → Groups → Roles → Permissions)
 *    "What can you do?" - Based on your role assignments
 * 
 * 2. SCOPE LAYER (Org Chart / Hierarchy)
 *    "Where can you do it?" - Based on where you have roles assigned
 * 
 * 3. POLICY LAYER (State, Timing, Ownership)
 *    "Under what conditions?" - Even if you CAN do it, are the conditions met?
 * 
 * ALL THREE must pass for an action to be allowed.
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { isImpersonationEnabled } from '../../../lib/impersonationConfig';
import {
  Shield,
  CheckCircle,
  XCircle,
  Lock,
  FileText,
  Settings,
  Play,
  RefreshCw,
  Info,
  AlertTriangle,
  Users,
  GitBranch,
  Clock,
  ArrowRight,
  ChevronRight,
  Building2,
  UserCheck,
  Search,
  X,
  UserCog,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useIsAdmin } from '../../../hooks/prioritization/useAuthorization';
import { DisabledReason, DisabledReasonBadge } from '../../../components/prioritization/DisabledReason';
import { AuthorizationGate } from '../../../components/prioritization/AuthorizationGate';
import { buildResourceContext, checkAuthorization } from '../../../lib/authorization/enforce';
import { getRuleStats, POLICY_RULES } from '../../../lib/authorization/policyRules';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { getRoleAssignments } from '../../../lib/rolesService';
import { getSafeSupabaseClient } from '../../../lib/supabaseSync';
import type { ResourceContext, EvaluationContext } from '../../../lib/authorization/types';

// ── Types ────────────────────────────────────────────────────────────────────
interface WorkspaceUser {
  id: string;
  name: string;
  email: string;
  role?: string;
}

export default function AuthorizationDemo() {
  const userStore = useUserStore();
  const isAdmin = useIsAdmin();
  const ruleStats = getRuleStats();
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);

  // ── "Test As User" panel state ──────────────────────────────────────────
  const [wsUsers, setWsUsers] = useState<WorkspaceUser[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [userPanelOpen, setUserPanelOpen] = useState(false);

  const fetchWsUsers = useCallback(async () => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    setUsersLoading(true);
    try {
      // Get members of the current workspace
      const membershipsQuery = workspaceId
        ? supabase.from('workspace_memberships').select('user_id').eq('workspace_id', workspaceId)
        : supabase.from('workspace_memberships').select('user_id').limit(200);
      const { data: memberships } = await membershipsQuery;
      const ids = [...new Set((memberships ?? []).map((m: { user_id: string }) => m.user_id))];
      if (ids.length === 0) { setWsUsers([]); return; }

      const { data: profiles } = await supabase
        .from('users')
        .select('id, name, email')
        .in('id', ids)
        .order('name', { ascending: true })
        .limit(100);

      // Fetch primary role for each user
      const { data: assignments } = await supabase
        .from('role_assignments')
        .select('subject_id, roles:role_id(name)')
        .eq('subject_type', 'user')
        .in('subject_id', ids)
        .eq('active', true);
      const roleMap = new Map<string, string>();
      (assignments ?? []).forEach((a: { subject_id: string; roles?: { name: string } | null }) => {
        if (a?.roles && !roleMap.has(a.subject_id)) roleMap.set(a.subject_id, a.roles.name);
      });

      setWsUsers((profiles ?? []).map((u: { id: string; name: string; email: string }) => ({
        id: u.id,
        name: u.name || u.email,
        email: u.email,
        role: roleMap.get(u.id),
      })));
    } catch (err) {
      console.error('[AuthDemo] Failed to load workspace users:', err);
    } finally {
      setUsersLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (userPanelOpen && wsUsers.length === 0) fetchWsUsers();
  }, [userPanelOpen, wsUsers.length, fetchWsUsers]);

  const viewAsOn = isImpersonationEnabled();

  const handleImpersonate = useCallback(async (u: WorkspaceUser) => {
    userStore.startImpersonation(u.id, u.name, u.email, u.role ?? 'strategist');
    setUserPanelOpen(false);
    setUserSearch('');
    // Load permissions for the impersonated user (no page reload needed)
    await userStore.loadPermissions();
  }, [userStore]);

  const handleRestoreUser = useCallback(async () => {
    userStore.stopImpersonation();
    setUserPanelOpen(false);
    await userStore.loadPermissions();
  }, [userStore]);

  const filteredWsUsers = useMemo(() => {
    const q = userSearch.toLowerCase();
    return wsUsers.filter(
      (u) => !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.role?.toLowerCase().includes(q)
    );
  }, [wsUsers, userSearch]);

  // Load real org units from the store
  const { orgUnits: orgUnitsMap, fetchFromSupabase: fetchOrgUnits } = useEnterpriseIdentityStore();
  const orgUnitsList = useMemo(() => Array.from(orgUnitsMap.values()), [orgUnitsMap]);
  
  // User's actual scoped role assignments
  const [userScopedAssignments, setUserScopedAssignments] = useState<{
    scopeKind: string;
    scopeId: string;
    roleId: string;
  }[]>([]);
  
  // Load user's role assignments from database
  useEffect(() => {
    fetchOrgUnits();
    
    const loadAssignments = async () => {
      try {
        const assignments = await getRoleAssignments({
          subjectType: 'user',
          subjectId: userStore.userId,
          activeOnly: true,
        });
        setUserScopedAssignments(assignments.map(a => ({
          scopeKind: a.scopeKind,
          scopeId: a.scopeId,
          roleId: a.roleId,
        })));
      } catch (err) {
        console.error('Failed to load role assignments:', err);
      }
    };
    loadAssignments();
  }, [userStore.userId]);
  
  // Test resource configuration
  const [testResource, setTestResource] = useState({
    type: 'item' as const,
    id: 'test-item-123',
    createdBy: userStore.userId, // Start as owner
    assigneeId: '',
    lifecycleState: 'draft',
    isLocked: false,
    isPublished: false,
    orgUnitId: '', // Will be set to first available org unit
  });
  
  // Set default org unit when loaded
  useEffect(() => {
    if (orgUnitsList.length > 0 && !testResource.orgUnitId) {
      const firstTeam = orgUnitsList.find(o => o.type === 'team') || orgUnitsList[0];
      setTestResource(prev => ({ ...prev, orgUnitId: firstTeam.id }));
    }
  }, [orgUnitsList]);
  
  // Test context configuration
  const [testContext, setTestContext] = useState({
    phase: 'shaping',
    submissionWindowOpen: true,
    contributionsFrozen: false,
  });

  // V2 prerequisite toggles (true / false / absent) for criteria and values actions
  type PrereqToggle = 'true' | 'false' | 'absent';
  const [v2Prereqs, setV2Prereqs] = useState<{
    criteriaLocked: PrereqToggle;
    valuesSufficient: PrereqToggle;
  }>({ criteriaLocked: 'true', valuesSufficient: 'true' });

  // Last decision from a v2-native action button (for display)
  const [lastV2Decision, setLastV2Decision] = useState<{
    action: string;
    decision: { allowed: boolean; engine?: string; ruleId?: string; reasons?: { code: string }[] };
  } | null>(null);
  
  // Action to test
  const [testAction, setTestAction] = useState('items.edit.content');
  
  // Simulated user scope selection (for testing what-if scenarios)
  const [userOrgUnit, setUserOrgUnit] = useState('');
  
  // Compute scope check based on real role assignments
  const scopeCheckPassed = useMemo(() => {
    // Check if user has any role assignment that covers the resource's org unit
    for (const assignment of userScopedAssignments) {
      // Global scope (*) covers everything
      if (assignment.scopeId === '*') return true;
      
      // Organization-level scope covers everything
      if (assignment.scopeKind === 'organization') return true;
      
      // Direct match
      if (assignment.scopeId === testResource.orgUnitId) return true;
      
      // Check if resource's org unit is a descendant of the assignment's scope
      const resourceOrgUnit = orgUnitsMap.get(testResource.orgUnitId);
      if (resourceOrgUnit && resourceOrgUnit.path.includes(assignment.scopeId as any)) {
        return true;
      }
    }
    
    // Also check the simulated user scope selection
    if (userOrgUnit === 'organization' || userOrgUnit === testResource.orgUnitId) {
      return true;
    }
    
    return false;
  }, [userScopedAssignments, testResource.orgUnitId, userOrgUnit, orgUnitsMap]);
  
  // Build resource context from test data
  const resourceContext: ResourceContext = useMemo(() => ({
    type: testResource.type,
    id: testResource.id,
    createdBy: testResource.createdBy,
    assigneeId: testResource.assigneeId || undefined,
    lifecycleState: testResource.lifecycleState,
    lockState: testResource.isLocked ? { isLocked: true } : undefined,
    isPublished: testResource.isPublished,
  }), [testResource]);
  
  // Build evaluation context (includes v2 prerequisites when toggled)
  const evalContext: Partial<EvaluationContext> = useMemo(() => {
    const prereqs: EvaluationContext['prerequisites'] = {};
    if (v2Prereqs.criteriaLocked !== 'absent') {
      prereqs.criteriaLocked = v2Prereqs.criteriaLocked === 'true';
    }
    if (v2Prereqs.valuesSufficient !== 'absent') {
      prereqs.valuesSufficient = v2Prereqs.valuesSufficient === 'true';
    }
    return {
      phase: testContext.phase,
      submissionWindowOpen: testContext.submissionWindowOpen,
      contributionsFrozen: testContext.contributionsFrozen,
      ...(Object.keys(prereqs).length > 0 && { prerequisites: prereqs }),
    };
  }, [testContext, v2Prereqs]);
  
  // Run the real enforcement path (V1+V2 routing, authored policies, resolvedCapabilityIds)
  // useMemo re-runs whenever action/resource/context or identity state changes
  const testDecision = useMemo(
    () => checkAuthorization(testAction, resourceContext, evalContext),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      testAction,
      resourceContext,
      evalContext,
      userStore.userId,
      userStore.currentRole,
      userStore.resolvedCapabilityIds,
      userStore.assignedRoleNames,
    ]
  );

  // Sample items for demonstration
  const sampleItems = [
    { 
      id: 'item-1', 
      title: 'Your Draft Item', 
      data: { createdBy: userStore.userId, lifecycleState: 'draft' } 
    },
    { 
      id: 'item-2', 
      title: "Someone Else's Item", 
      data: { createdBy: 'other-user', lifecycleState: 'draft' } 
    },
    { 
      id: 'item-3', 
      title: 'Locked Item', 
      data: { createdBy: userStore.userId, lockState: { isLocked: true } } 
    },
    { 
      id: 'item-4', 
      title: 'Published Item', 
      data: { createdBy: userStore.userId, isPublished: true } 
    },
  ];
  
  // Combined authorization result considering all three layers
  // capabilityPassed uses the full action string so it matches what checkAuthorization evaluates
  const capabilityPassed = testDecision.capabilityPassed;
  const policyPassed = testDecision.allowed;
  const allLayersPassed = capabilityPassed && scopeCheckPassed && policyPassed;
  
  return (
    <div className="space-y-8">
      <PageHeader
        icon={<Shield className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Authorization Diagnostics"
        subtitle="Three-layer authorization model: Capability + Scope + Policy = Access Decision"
      />
      
      {/* Three Layer Architecture Overview */}
      <div className="bg-[var(--slate-dim)] border-2 border-[var(--slate)] p-6">
        <h2 className="text-lg font-semibold mb-4 text-center">How Authorization Works</h2>
        
        <div className="flex flex-col md:flex-row items-center justify-center gap-4">
          {/* Layer 1: Capability */}
          <div className="flex-1 max-w-xs">
            <div className={` p-4 border-2 ${
              capabilityPassed ? 'bg-[var(--green-dim)] border-[var(--green)]' : 'bg-[var(--coral-dim)] border-[var(--coral)]'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-5 w-5 text-[var(--text-ter)]" />
                <span className="font-semibold">1. Capability</span>
                {capabilityPassed ? (
                  <CheckCircle className="h-4 w-4 text-[var(--green)] ml-auto" />
                ) : (
                  <XCircle className="h-4 w-4 text-[var(--coral)] ml-auto" />
                )}
              </div>
              <div className="text-sm text-[var(--text-sec)] mb-2">"What can you do?"</div>
              <div className="text-xs bg-[var(--bg-raised)]/50 rounded p-2">
                <div>User → Groups → Roles → Permissions</div>
                <div className="mt-1 font-mono text-[var(--text-ter)]">
                  {userStore.roles?.join(', ') || 'No roles'}
                </div>
              </div>
            </div>
          </div>
          
          <ChevronRight className="h-6 w-6 text-[var(--text-ter)] hidden md:block" />
          <ArrowRight className="h-6 w-6 text-[var(--text-ter)] md:hidden rotate-90" />
          
          {/* Layer 2: Scope */}
          <div className="flex-1 max-w-xs">
            <div className={` p-4 border-2 ${
              scopeCheckPassed ? 'bg-[var(--green-dim)] border-[var(--green)]' : 'bg-[var(--coral-dim)] border-[var(--coral)]'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="h-5 w-5 text-[var(--text-ter)]" />
                <span className="font-semibold">2. Scope</span>
                {scopeCheckPassed ? (
                  <CheckCircle className="h-4 w-4 text-[var(--green)] ml-auto" />
                ) : (
                  <XCircle className="h-4 w-4 text-[var(--coral)] ml-auto" />
                )}
              </div>
              <div className="text-sm text-[var(--text-sec)] mb-2">"Where can you do it?"</div>
              <div className="text-xs bg-[var(--bg-raised)]/50 rounded p-2">
                <div>Org Unit hierarchy check</div>
                <div className="mt-1">
                  Your scope: <span className="font-mono text-[var(--text-ter)]">{userOrgUnit}</span>
                </div>
                <div>
                  Resource in: <span className="font-mono text-[var(--text-ter)]">{testResource.orgUnitId}</span>
                </div>
              </div>
            </div>
          </div>
          
          <ChevronRight className="h-6 w-6 text-[var(--text-ter)] hidden md:block" />
          <ArrowRight className="h-6 w-6 text-[var(--text-ter)] md:hidden rotate-90" />
          
          {/* Layer 3: Policy */}
          <div className="flex-1 max-w-xs">
            <div className={` p-4 border-2 ${
              policyPassed ? 'bg-[var(--green-dim)] border-[var(--green)]' : 'bg-[var(--coral-dim)] border-[var(--coral)]'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-5 w-5 text-[var(--coral)]" />
                <span className="font-semibold">3. Policy</span>
                {policyPassed ? (
                  <CheckCircle className="h-4 w-4 text-[var(--green)] ml-auto" />
                ) : (
                  <XCircle className="h-4 w-4 text-[var(--coral)] ml-auto" />
                )}
              </div>
              <div className="text-sm text-[var(--text-sec)] mb-2">"Under what conditions?"</div>
              <div className="text-xs bg-[var(--bg-raised)]/50 rounded p-2">
                <div>State, timing, ownership rules</div>
                <div className="mt-1 font-mono text-[var(--coral)]">
                  {testDecision.ruleId || 'checking...'}
                </div>
              </div>
            </div>
          </div>
          
          <ChevronRight className="h-6 w-6 text-[var(--text-ter)] hidden md:block" />
          <ArrowRight className="h-6 w-6 text-[var(--text-ter)] md:hidden rotate-90" />
          
          {/* Final Result */}
          <div className="flex-1 max-w-xs">
            <div className={` p-4 border-2 ${
              allLayersPassed ? 'bg-[var(--green-dim)] border-[var(--green)]' : 'bg-[var(--coral-dim)] border-[var(--coral)]'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {allLayersPassed ? (
                  <UserCheck className="h-5 w-5 text-[var(--green)]" />
                ) : (
                  <XCircle className="h-5 w-5 text-[var(--coral)]" />
                )}
                <span className="font-semibold">Result</span>
              </div>
              <div className={`text-lg font-bold ${allLayersPassed ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--coral)]'}`}>
                {allLayersPassed ? 'ALLOWED' : 'DENIED'}
              </div>
              <div className="text-xs mt-1 text-[var(--text-sec)]">
                {!capabilityPassed && 'Missing permission'}
                {capabilityPassed && !scopeCheckPassed && 'Outside your scope'}
                {capabilityPassed && scopeCheckPassed && !policyPassed && 'Policy conditions not met'}
                {allLayersPassed && 'All checks passed'}
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-4 text-center text-sm text-[var(--text-sec)]">
          All three layers must pass for an action to be allowed
        </div>
      </div>
      
      {/* Test As User panel */}
      <div className="bg-[var(--bg-raised)] border p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <UserCog className="h-5 w-5 text-[var(--text-ter)]" />
            {userStore.isImpersonating ? 'Testing as another user' : 'Current User Context'}
          </h2>
          <div className="flex items-center gap-2">
            {userStore.isImpersonating && (
              <button
                onClick={handleRestoreUser}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--mustard-dim)] border border-[var(--mustard)] text-[var(--mustard)] hover:bg-[var(--mustard-dim)] transition-colors"
              >
                <X className="h-3.5 w-3.5" />
                Restore ({userStore.originalUserName})
              </button>
            )}
            {/* View-as is switched off (src/lib/impersonationConfig.ts): the store refuses
                startImpersonation, so a "Test as user" button would do nothing. */}
            {viewAsOn ? (
            <div className="relative">
              <button
                onClick={() => setUserPanelOpen((o) => !o)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--slate-dim)] border border-[var(--slate)] text-[var(--text-sec)] hover:bg-[var(--slate-dim)] transition-colors"
              >
                <Users className="h-3.5 w-3.5" />
                Test as user
                <ChevronRight className={`h-3.5 w-3.5 transition-transform ${userPanelOpen ? 'rotate-90' : ''}`} />
              </button>

              {userPanelOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setUserPanelOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 w-72 bg-[var(--bg-raised)] shadow-lg border border-[var(--rule)] z-50 overflow-hidden">
                    <div className="p-3 border-b border-[var(--rule)]">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
                        <input
                          type="text"
                          placeholder="Search users…"
                          value={userSearch}
                          onChange={(e) => setUserSearch(e.target.value)}
                          autoFocus
                          className="w-full pl-9 pr-3 py-2 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-indigo-300 focus:border-[var(--slate)]"
                        />
                      </div>
                    </div>
                    <div className="overflow-y-auto max-h-64 p-2">
                      {usersLoading ? (
                        <div className="py-6 text-center text-sm text-[var(--text-ter)]">Loading users…</div>
                      ) : filteredWsUsers.length === 0 ? (
                        <div className="py-6 text-center text-sm text-[var(--text-ter)]">No users found</div>
                      ) : (
                        filteredWsUsers.map((u) => (
                          <button
                            key={u.id}
                            onClick={() => handleImpersonate(u)}
                            disabled={u.id === userStore.userId}
                            className="w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-[var(--surface)] text-left disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            <div className="w-7 h-7 rounded-full bg-[var(--slate-dim)] text-[var(--text-sec)] flex items-center justify-center flex-shrink-0 text-xs font-semibold">
                              {u.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-[var(--text-pri)] truncate font-medium">
                                {u.name}
                                {u.id === userStore.userId && <span className="ml-1.5 text-xs text-[var(--text-ter)]">(you)</span>}
                              </div>
                              {u.role && <div className="text-xs text-[var(--text-ter)] truncate">{u.role}</div>}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                    <div className="p-2 border-t border-[var(--rule)] bg-[var(--surface)] text-center">
                      <span className="text-xs text-[var(--text-ter)]">Switches authorization context — no page reload</span>
                    </div>
                  </div>
                </>
              )}
            </div>
            ) : (
              <span className="text-sm text-[var(--text-ter)]">View-as is switched off for this workspace.</span>
            )}
          </div>
        </div>

        {userStore.isImpersonating && (
          <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-sm text-[var(--mustard)]">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <span>
              Authorization results below reflect <strong>{userStore.userName}</strong>'s permissions, not yours.
            </span>
          </div>
        )}

        {userStore.permissionsLoading && (
          <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-[var(--slate-dim)] border border-[var(--slate)] text-sm text-[var(--text-sec)]">
            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            Loading permissions for {userStore.userName}…
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-[var(--surface)] p-3">
            <div className="text-xs text-[var(--text-ter)] uppercase">User</div>
            <div className="font-medium text-sm truncate">{userStore.userName || '—'}</div>
            <div className="font-mono text-xs text-[var(--text-ter)] truncate">{userStore.userId}</div>
          </div>
          <div className="bg-[var(--surface)] p-3">
            <div className="text-xs text-[var(--text-ter)] uppercase">Roles</div>
            <div className="text-sm">{userStore.roles?.join(', ') || 'None'}</div>
          </div>
          <div className="bg-[var(--surface)] p-3">
            <div className="text-xs text-[var(--text-ter)] uppercase">Current Role</div>
            <div className="text-sm font-mono">{userStore.currentRole ?? '—'}</div>
          </div>
          <div className="bg-[var(--surface)] p-3">
            <div className="text-xs text-[var(--text-ter)] uppercase">Admin Override</div>
            <div className={`text-sm font-medium ${isAdmin ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-sec)]'}`}>
              {isAdmin ? 'Active' : 'None'}
            </div>
          </div>
        </div>
      </div>
      
      {/* Policy Stats */}
      <div className="bg-[var(--bg-raised)] border p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Policy Rules Summary
        </h2>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-4 bg-[var(--surface)]">
            <div className="text-3xl font-bold text-[var(--text-pri)]">{ruleStats.total}</div>
            <div className="text-sm text-[var(--text-ter)]">Total Rules</div>
          </div>
          <div className="text-center p-4 bg-[var(--green-dim)]">
            <div className="text-3xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{ruleStats.allowRules}</div>
            <div className="text-sm text-[var(--text-ter)]">Allow Rules</div>
          </div>
          <div className="text-center p-4 bg-[var(--coral-dim)]">
            <div className="text-3xl font-bold text-[var(--coral)]">{ruleStats.denyRules}</div>
            <div className="text-sm text-[var(--text-ter)]">Deny Rules</div>
          </div>
        </div>
        <details className="text-sm">
          <summary className="cursor-pointer text-[var(--text-ter)] hover:text-[var(--text-ter)]">
            View all policy rules ({POLICY_RULES.length})
          </summary>
          <div className="mt-2 space-y-2 max-h-64 overflow-y-auto">
            {POLICY_RULES.map((rule, index) => (
              <div key={rule.id} className="flex items-center gap-2 p-2 bg-[var(--surface)] rounded text-xs">
                <span className="text-[var(--text-ter)] w-6">{index + 1}.</span>
                <span className={`px-2 py-0.5 rounded ${
                  rule.effect === 'allow' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--coral-dim)] text-[var(--coral)]'
                }`}>
                  {rule.effect}
                </span>
                <span className="font-mono flex-1">{rule.id}</span>
                <span className="text-[var(--text-ter)]">{rule.actions.join(', ')}</span>
              </div>
            ))}
          </div>
        </details>
      </div>
      
      {/* Interactive Test */}
      <div className="bg-[var(--bg-raised)] border p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Play className="h-5 w-5" />
          Interactive Authorization Test
        </h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Configuration */}
          <div className="space-y-4">
            <h3 className="font-medium text-[var(--text-sec)]">Configure Test Scenario</h3>
            
            {/* Action */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Action</label>
              <select
                value={testAction}
                onChange={(e) => setTestAction(e.target.value)}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border px-3 py-2"
              >
                <optgroup label="Item Actions">
                  <option value="items.edit.content">items.edit.content</option>
                  <option value="items.edit.status">items.edit.status</option>
                  <option value="items.delete">items.delete</option>
                  <option value="items.edit">items.edit</option>
                  <option value="items.view">items.view</option>
                  <option value="items.create">items.create</option>
                </optgroup>
                <optgroup label="List Actions">
                  <option value="lists.publish">lists.publish</option>
                  <option value="lists.move_cutline">lists.move_cutline</option>
                  <option value="lists.edit">lists.edit</option>
                </optgroup>
                <optgroup label="Session Actions">
                  <option value="session.manage">session.manage</option>
                  <option value="session.participate">session.participate</option>
                </optgroup>
              </select>
            </div>
            
            {/* Resource Owner */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Resource Owner</label>
              <select
                value={testResource.createdBy === userStore.userId ? 'me' : 'other'}
                onChange={(e) => setTestResource({
                  ...testResource,
                  createdBy: e.target.value === 'me' ? userStore.userId : 'other-user-456'
                })}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border px-3 py-2"
              >
                <option value="me">You (owner)</option>
                <option value="other">Someone else</option>
              </select>
            </div>
            
            {/* Steward */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Item Owner (Assignee)</label>
              <select
                value={testResource.assigneeId === userStore.userId ? 'me' : testResource.assigneeId ? 'other' : 'none'}
                onChange={(e) => setTestResource({
                  ...testResource,
                  assigneeId: e.target.value === 'me' ? userStore.userId : e.target.value === 'other' ? 'other-assignee' : ''
                })}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border px-3 py-2"
              >
                <option value="none">No assignee</option>
                <option value="me">You (assigned)</option>
                <option value="other">Someone else</option>
              </select>
            </div>
            
            {/* Scope Configuration */}
            <div className="pt-2 border-t">
              <h4 className="text-sm font-medium text-[var(--text-sec)] mb-2 flex items-center gap-1">
                <Building2 className="h-4 w-4" />
                Scope (Layer 2)
              </h4>
              
              {/* Show user's actual scoped assignments */}
              {userScopedAssignments.length > 0 && (
                <div className="mb-2 text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] px-2 py-1 rounded">
                  Your scopes: {userScopedAssignments.map(a => 
                    a.scopeId === '*' ? `${a.scopeKind} (all)` : 
                    orgUnitsMap.get(a.scopeId)?.displayName || a.scopeId
                  ).join(', ')}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-[var(--text-ter)]">Simulate Your Scope</label>
                  <select
                    value={userOrgUnit}
                    onChange={(e) => setUserOrgUnit(e.target.value)}
                    className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border rounded px-2 py-1 text-sm"
                  >
                    <option value="">-- Use actual assignments --</option>
                    <option value="organization">Organization (all)</option>
                    {orgUnitsList.map(ou => (
                      <option key={ou.id} value={ou.id}>{ou.displayName}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[var(--text-ter)]">Resource Org Unit</label>
                  <select
                    value={testResource.orgUnitId}
                    onChange={(e) => setTestResource({ ...testResource, orgUnitId: e.target.value })}
                    className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border rounded px-2 py-1 text-sm"
                  >
                    {orgUnitsList.length === 0 && <option value="">Loading...</option>}
                    {orgUnitsList.map(ou => (
                      <option key={ou.id} value={ou.id}>{ou.displayName}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className={`mt-2 text-xs px-2 py-1 rounded ${
                scopeCheckPassed ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--coral-dim)] text-[var(--coral)]'
              }`}>
                {scopeCheckPassed 
                  ? '✓ Resource is within your scope' 
                  : '✗ Resource is outside your scope'}
              </div>
              <p className="mt-2 text-xs text-[var(--text-ter)] italic">
                Scope is a what-if simulation. The enforcement engine (Layers 1 &amp; 3) does not yet propagate org-unit scope into its decisions.
              </p>
            </div>
            
            {/* State toggles */}
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={testResource.isLocked}
                  onChange={(e) => setTestResource({ ...testResource, isLocked: e.target.checked })}
                  className="rounded"
                />
                <Lock className="h-4 w-4" />
                Locked
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={testResource.isPublished}
                  onChange={(e) => setTestResource({ ...testResource, isPublished: e.target.checked })}
                  className="rounded"
                />
                <FileText className="h-4 w-4" />
                Published
              </label>
            </div>
            
            {/* Context toggles */}
            <div className="pt-2 border-t">
              <h4 className="text-sm font-medium text-[var(--text-sec)] mb-2">Context</h4>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={testContext.submissionWindowOpen}
                    onChange={(e) => setTestContext({ ...testContext, submissionWindowOpen: e.target.checked })}
                    className="rounded"
                  />
                  Submissions Open
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={testContext.contributionsFrozen}
                    onChange={(e) => setTestContext({ ...testContext, contributionsFrozen: e.target.checked })}
                    className="rounded"
                  />
                  Frozen
                </label>
              </div>
              <div className="mt-2">
                <label className="block text-sm text-[var(--text-sec)]">Phase</label>
                <select
                  value={testContext.phase}
                  onChange={(e) => setTestContext({ ...testContext, phase: e.target.value })}
                  className="bg-[var(--bg-raised)] text-[var(--text-pri)] border rounded px-2 py-1 text-sm"
                >
                  <option value="discovery">Discovery</option>
                  <option value="shaping">Item work</option>
                  <option value="arrange">Arrange</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Result */}
          <div>
            <h3 className="font-medium text-[var(--text-sec)] mb-4">Combined Authorization Result</h3>
            
            {/* Three-Layer Summary */}
            <div className="space-y-2 mb-4">
              <div className={`flex items-center justify-between p-2 rounded ${
                capabilityPassed ? 'bg-[var(--green-dim)]' : 'bg-[var(--coral-dim)]'
              }`}>
                <span className="flex items-center gap-2 text-sm">
                  <Users className="h-4 w-4" />
                  Layer 1: Capability
                </span>
                <span className={`font-medium ${capabilityPassed ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--coral)]'}`}>
                  {capabilityPassed ? '✓ Pass' : '✗ Fail'}
                </span>
              </div>
              <div className={`flex items-center justify-between p-2 rounded ${
                scopeCheckPassed ? 'bg-[var(--green-dim)]' : 'bg-[var(--coral-dim)]'
              }`}>
                <span className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4" />
                  Layer 2: Scope
                </span>
                <span className={`font-medium ${scopeCheckPassed ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--coral)]'}`}>
                  {scopeCheckPassed ? '✓ Pass' : '✗ Fail'}
                </span>
              </div>
              <div className={`flex items-center justify-between p-2 rounded ${
                policyPassed ? 'bg-[var(--green-dim)]' : 'bg-[var(--coral-dim)]'
              }`}>
                <span className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4" />
                  Layer 3: Policy
                </span>
                <span className={`font-medium ${policyPassed ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--coral)]'}`}>
                  {policyPassed ? '✓ Pass' : '✗ Fail'}
                </span>
              </div>
            </div>
            
            {/* Final Decision */}
            <div className={` p-6 ${
              allLayersPassed 
                ? 'bg-[var(--green-dim)] border-2 border-[var(--green)]' 
                : 'bg-[var(--coral-dim)] border-2 border-[var(--coral)]'
            }`}>
              <div className="flex items-center gap-3 mb-4">
                {allLayersPassed ? (
                  <CheckCircle className="h-8 w-8 text-[var(--green)]" />
                ) : (
                  <XCircle className="h-8 w-8 text-[var(--coral)]" />
                )}
                <div>
                  <div className={`text-xl font-bold ${allLayersPassed ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--coral)]'}`}>
                    {allLayersPassed ? 'ALLOWED' : 'DENIED'}
                  </div>
                  <div className="text-sm text-[var(--text-sec)]">
                    Action: <code className="bg-[var(--bg-raised)]/50 px-1 rounded">{testAction}</code>
                  </div>
                </div>
              </div>
              
              {/* Failure Explanation */}
              {!allLayersPassed && (
                <div className="text-sm space-y-2">
                  {!capabilityPassed && (
                    <div className="p-2 bg-[var(--coral-dim)] rounded">
                      <strong>Capability Failed:</strong> Your role does not grant the <code>{testAction}</code> permission.
                    </div>
                  )}
                  {capabilityPassed && !scopeCheckPassed && (
                    <div className="p-2 bg-[var(--coral-dim)] rounded">
                      <strong>Scope Failed:</strong> The resource is in <code>{testResource.orgUnitId}</code> but your role is scoped to <code>{userOrgUnit}</code>.
                    </div>
                  )}
                  {capabilityPassed && scopeCheckPassed && !policyPassed && (
                    <div className="p-2 bg-[var(--coral-dim)] rounded">
                      <strong>Policy Failed:</strong> {testDecision.reasons[0]?.code}
                      {testDecision.reasons[0]?.data && (
                        <span className="text-xs ml-2">
                          ({JSON.stringify(testDecision.reasons[0].data)})
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Policy Details */}
              <div className="mt-4 pt-4 border-t space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[var(--text-sec)]">Policy Rule:</span>
                  <code className="font-mono">{testDecision.ruleId}</code>
                </div>
                <div className="pt-2 border-t">
                  <span className="text-[var(--text-sec)]">Reason Codes:</span>
                  <ul className="mt-1 space-y-1">
                    {testDecision.reasons.map((reason, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-[var(--bg-raised)]/50 rounded font-mono text-xs">
                          {reason.code}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {!allLayersPassed && (
                <div className="mt-4 pt-4 border-t">
                  <DisabledReason decision={testDecision} style="block" showHints />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Live Examples */}
      <div className="bg-[var(--bg-raised)] border p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Live Authorization Examples
        </h2>
        <p className="text-sm text-[var(--text-sec)] mb-4">
          These examples show how the <code>AuthorizationGate</code> component works with real items.
          Buttons are disabled or hidden based on your permissions.
        </p>
        
        <div className="space-y-4">
          {sampleItems.map((item) => {
            const resource = buildResourceContext('item', item.id, item.data);
            const deleteDecision = checkAuthorization('items.delete', resource, evalContext);
            
            return (
              <div key={item.id} className="flex items-center justify-between p-4 bg-[var(--surface)]">
                <div>
                  <div className="font-medium">{item.title}</div>
                  <div className="text-xs text-[var(--text-ter)]">
                    Owner: {item.data.createdBy === userStore.userId ? 'You' : 'Other'} | 
                    State: {item.data.lifecycleState || 'draft'} |
                    {item.data.lockState?.isLocked && ' Locked |'}
                    {item.data.isPublished && ' Published'}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Edit button with authorization */}
                  <AuthorizationGate 
                    action="items.edit.content" 
                    resource={resource}
                    context={evalContext}
                    disableInsteadOfHide
                    showReason
                  >
                    <button className="px-3 py-1 text-sm bg-[var(--slate)] text-white rounded hover:bg-[var(--slate)]">
                      Edit
                    </button>
                  </AuthorizationGate>
                  
                  {/* Delete button - show badge if denied */}
                  {deleteDecision.allowed ? (
                    <button className="px-3 py-1 text-sm bg-[var(--coral)] text-white rounded hover:bg-[var(--coral)]">
                      Delete
                    </button>
                  ) : (
                    <div className="flex items-center gap-1">
                      <button 
                        disabled 
                        className="px-3 py-1 text-sm bg-[var(--rule-bright)] text-[var(--text-ter)] rounded cursor-not-allowed"
                      >
                        Delete
                      </button>
                      <DisabledReasonBadge decision={deleteDecision} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* V2-Native Actions (Prerequisites) */}
      <div className="bg-[var(--bg-raised)] border p-6">
        <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
          <Settings className="h-5 w-5" />
          V2-Native Actions (Prerequisites)
        </h2>
        <p className="text-sm text-[var(--text-sec)] mb-4">
          Test <code>checkAuthorization</code> for v2-native actions. Toggle prerequisites to force allow/deny.
          values.accept uses engine v2-session (denies when valuesSufficient=false). criteria.* use engine v2-proposal-config (deny when criteriaLocked=false).
        </p>

        {/* Prerequisite toggles */}
        <div className="mb-4 p-4 bg-[var(--surface)]">
          <h4 className="text-sm font-medium text-[var(--text-sec)] mb-2">context.prerequisites</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[var(--text-sec)] mb-1">criteriaLocked</label>
              <select
                value={v2Prereqs.criteriaLocked}
                onChange={(e) => setV2Prereqs((p) => ({ ...p, criteriaLocked: e.target.value as PrereqToggle }))}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] border rounded px-2 py-1 text-sm w-full"
              >
                <option value="true">true</option>
                <option value="false">false</option>
                <option value="absent">absent</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-[var(--text-sec)] mb-1">valuesSufficient</label>
              <select
                value={v2Prereqs.valuesSufficient}
                onChange={(e) => setV2Prereqs((p) => ({ ...p, valuesSufficient: e.target.value as PrereqToggle }))}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] border rounded px-2 py-1 text-sm w-full"
              >
                <option value="true">true</option>
                <option value="false">false</option>
                <option value="absent">absent</option>
              </select>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          {(['values.accept', 'criteria.edit', 'criteria.create', 'criteria.delete'] as const).map((action) => {
            const resource = buildResourceContext('session', 'test-session-v2', { scope: { kind: 'organization' as const, id: '*' } });
            return (
              <button
                key={action}
                type="button"
                onClick={() => {
                  const decision = checkAuthorization(action, resource, evalContext);
                  setLastV2Decision({
                    action,
                    decision: {
                      allowed: decision.allowed,
                      engine: (decision as { engine?: string }).engine,
                      ruleId: decision.ruleId,
                      reasons: decision.reasons,
                    },
                  });
                }}
                className="px-4 py-2 text-sm bg-[var(--slate-dim)] text-[var(--text-sec)] hover:bg-[var(--slate-dim)]"
              >
                {action}
              </button>
            );
          })}
        </div>

        {/* Last decision JSON */}
        {lastV2Decision && (
          <div className="p-4 bg-[var(--surface)] border">
            <h4 className="text-sm font-medium text-[var(--text-sec)] mb-2">Last decision: {lastV2Decision.action}</h4>
            <pre className="text-xs overflow-x-auto text-[var(--text-pri)]">
              {JSON.stringify(lastV2Decision.decision, null, 2)}
            </pre>
          </div>
        )}
      </div>
      
      {/* Three-Layer Architecture Explanation */}
      <div className="bg-[var(--bg-raised)] border p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <GitBranch className="h-5 w-5 text-[var(--text-ter)]" />
          Three-Layer Authorization Architecture
        </h2>
        
        <div className="grid md:grid-cols-3 gap-6">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[var(--slate-dim)] flex items-center justify-center font-bold text-[var(--text-sec)]">1</div>
              <h3 className="font-medium">Capability Layer</h3>
            </div>
            <p className="text-sm text-[var(--text-sec)]">"What can you do?"</p>
            <ul className="text-xs space-y-1 text-[var(--text-ter)]">
              <li>• <strong>Users</strong> are assigned to <strong>Groups</strong></li>
              <li>• Groups are assigned <strong>Roles</strong></li>
              <li>• Roles grant <strong>Permissions</strong></li>
              <li>• Permissions enable <strong>Actions</strong></li>
            </ul>
            <div className="text-xs bg-[var(--slate-dim)] rounded p-2">
              <strong>Example:</strong> An "Item Manager" role grants <code>items.edit</code> permission
            </div>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[var(--slate-dim)] flex items-center justify-center font-bold text-[var(--text-sec)]">2</div>
              <h3 className="font-medium">Scope Layer</h3>
            </div>
            <p className="text-sm text-[var(--text-sec)]">"Where can you do it?"</p>
            <ul className="text-xs space-y-1 text-[var(--text-ter)]">
              <li>• Roles are assigned at <strong>Org Units</strong></li>
              <li>• Org Units form a <strong>hierarchy</strong></li>
              <li>• Permissions cascade <strong>downward</strong></li>
              <li>• Resources belong to Org Units</li>
            </ul>
            <div className="text-xs bg-[var(--slate-dim)] rounded p-2">
              <strong>Example:</strong> Item Manager role in "Product Team" only applies to items in that team
            </div>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[var(--coral-dim)] flex items-center justify-center font-bold text-[var(--coral)]">3</div>
              <h3 className="font-medium">Policy Layer</h3>
            </div>
            <p className="text-sm text-[var(--text-sec)]">"Under what conditions?"</p>
            <ul className="text-xs space-y-1 text-[var(--text-ter)]">
              <li>• <strong>Ownership:</strong> Is it yours?</li>
              <li>• <strong>State:</strong> Is it locked/published?</li>
              <li>• <strong>Timing:</strong> Is the window open?</li>
              <li>• <strong>Phase:</strong> Is this the right phase?</li>
            </ul>
            <div className="text-xs bg-[var(--coral-dim)] rounded p-2">
              <strong>Example:</strong> Can shape, in scope, but item is locked → DENIED
            </div>
          </div>
        </div>
        
        <div className="mt-6 p-4 bg-[var(--surface)]">
          <div className="text-sm font-medium text-[var(--text-sec)] mb-2">The Authorization Equation:</div>
          <div className="flex items-center gap-2 text-sm">
            <span className="px-3 py-1 bg-[var(--slate-dim)] rounded font-medium">Capability ✓</span>
            <span className="text-[var(--text-ter)]">+</span>
            <span className="px-3 py-1 bg-[var(--slate-dim)] rounded font-medium">Scope ✓</span>
            <span className="text-[var(--text-ter)]">+</span>
            <span className="px-3 py-1 bg-[var(--coral-dim)] rounded font-medium">Policy ✓</span>
            <span className="text-[var(--text-ter)]">=</span>
            <span className="px-3 py-1 bg-[var(--green-dim)] rounded font-bold text-[var(--green)] dark:text-[var(--text-ter)]">ACCESS GRANTED</span>
          </div>
          <div className="text-xs text-[var(--text-ter)] mt-2">
            If any layer fails, access is denied. The system reports which layer blocked the action.
          </div>
        </div>
      </div>
      
      {/* How to Use */}
      <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Info className="h-5 w-5 text-[var(--text-ter)]" />
          How It Works Together
        </h2>
        <div className="space-y-4 text-sm">
          <div>
            <h3 className="font-medium text-[var(--text-sec)] mb-1">1. UI Components (AuthorizationGate)</h3>
            <p className="text-xs text-[var(--text-sec)] mb-2">Checks all three layers before showing UI elements</p>
            <pre className="bg-[var(--bg-raised)] p-3 rounded border text-xs overflow-x-auto">
{`<AuthorizationGate action="items.edit" resource={resource}>
  <EditButton onClick={handleEdit} />
</AuthorizationGate>
// Only shows button if: capability ✓ + scope ✓ + policy ✓`}
            </pre>
          </div>
          <div>
            <h3 className="font-medium text-[var(--text-sec)] mb-1">2. React Hooks (useAuthorization)</h3>
            <p className="text-xs text-[var(--text-sec)] mb-2">Returns decision details including which layer failed</p>
            <pre className="bg-[var(--bg-raised)] p-3 rounded border text-xs overflow-x-auto">
{`const { allowed, decision, explain } = useAuthorization('items.edit', resource);
// decision.reasons tells you WHY it failed (e.g., RESOURCE_LOCKED, NOT_OWNER)`}
            </pre>
          </div>
          <div>
            <h3 className="font-medium text-[var(--text-sec)] mb-1">3. Store Enforcement (backend of stores)</h3>
            <p className="text-xs text-[var(--text-sec)] mb-2">Prevents mutations even if UI checks are bypassed</p>
            <pre className="bg-[var(--bg-raised)] p-3 rounded border text-xs overflow-x-auto">
{`// In itemsStore.ts
const decision = checkPolicyForItem('items.edit', id, itemData);
if (!decision.allowed) {
  // Dispatch event for UI notification, block the mutation
  return;
}`}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
